import * as React from "react";
import { useMergeRefs } from "@floating-ui/react";
import clsx from "clsx";
import { MediaQuery } from "react-responsive";
import debounce from "debounce";
import "tabbable";
import { Icon } from "../../icon/icon/icon.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { HeaderDropdown } from "../header-dropdown/header-dropdown.mjs";
import { HeaderMegamenu } from "../header-megamenu/header-megamenu.mjs";
const HeaderNavItem = (props) => {
  const { media } = useTheme();
  return /* @__PURE__ */ React.createElement(HeaderNavItemInner, { ...props, media });
};
class HeaderNavItemInner extends React.PureComponent {
  constructor(props) {
    super(props);
    this.labelRef = React.createRef();
    this.linkRef = React.createRef();
    this.handleResize = () => {
      if (this.labelRef.current) {
        this.resetItemStyle();
      }
    };
    this.handleMediaQueryChange = (matches) => {
      this.setState({
        isLargeScreen: matches
      });
    };
    this.debouncedResizeHandler = debounce(this.handleResize, 100);
    this.state = {
      isLargeScreen: false,
      isMounted: false,
      itemStyle: { minWidth: "1px" }
      // Some existing width is necessary for cross-browser support.
    };
  }
  componentDidMount() {
    this.setItemStyle();
    this.setState({
      isLargeScreen: window.matchMedia("(min-width: ".concat(this.props.media.width.lg.min, ")")).matches,
      isMounted: true
    });
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.label !== this.props.label && this.labelRef.current) {
      this.resetItemStyle();
    }
  }
  setItemStyle() {
    if (this.labelRef.current) {
      this.setState({
        itemStyle: {
          minWidth: "".concat(this.labelRef.current.scrollWidth, "px")
        }
      });
    }
  }
  resetItemStyle() {
    this.setState(
      {
        itemStyle: { minWidth: "1px" }
      },
      () => {
        this.setItemStyle();
      }
    );
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedResizeHandler);
    this.debouncedResizeHandler.clear();
  }
  BEM() {
    const classArray = ["header__nav-item"];
    if (this.props.isCurrent) {
      classArray.push("is-current");
      if (this.props.children) {
        classArray.push("has-submenu");
      }
    }
    if (this.props.isOpen) {
      classArray.push("is-open");
    }
    return classArray.join(" ");
  }
  renderMegamenuDropdown(megamenu) {
    return /* @__PURE__ */ React.createElement(
      HeaderDropdown,
      {
        showCloseButton: false,
        className: "header__nav-megamenu-dropdown",
        padding: "medium",
        reference: this.renderAnchor(),
        container: this.props.dropdownContainer || null,
        isShown: this.props.isOpen || false,
        onToggle: this.props.onToggle,
        id: "".concat(this.props.id, "-megamenu-dropdown"),
        fullWidth: true
      },
      this.renderMegamenu(megamenu, true)
    );
  }
  renderMegamenu(megamenu, setId = false) {
    return /* @__PURE__ */ React.createElement(HeaderMegamenu, { ...megamenu, className: "header__nav-megamenu", id: setId ? "".concat(this.props.id, "-megamenu") : void 0, reference: this.linkRef.current });
  }
  renderDropdownOrMegamenu() {
    const { children } = this.props;
    if (children) {
      if (this.state.isLargeScreen && this.props.dropdownContainer) {
        return this.renderMegamenuDropdown(children);
      }
      return /* @__PURE__ */ React.createElement(React.Fragment, null, this.renderSmallScreenAnchor(), this.renderMegamenu(children, true));
    }
    return null;
  }
  renderAnchor() {
    return /* @__PURE__ */ React.createElement(
      HeaderNavItemLink,
      {
        label: this.props.label,
        url: this.props.url,
        onClick: this.props.onClick,
        linkRef: this.linkRef,
        labelRef: this.labelRef,
        isCurrent: this.props.isCurrent,
        hasChildren: !!this.props.children,
        icon: this.props.icon,
        isMounted: this.state.isMounted,
        isLargeScreen: this.state.isLargeScreen,
        mobileLabel: this.props.mobileLabel,
        color: this.props.color,
        secondaryIcon: this.props.secondaryIcon,
        secondaryIconPurpose: this.props.secondaryIconPurpose,
        isOpen: this.props.isOpen,
        id: this.props.id
      }
    );
  }
  renderSmallScreenAnchor() {
    return /* @__PURE__ */ React.createElement(
      HeaderNavItemLink,
      {
        label: this.props.label,
        url: this.props.url,
        onClick: (event) => {
          event.preventDefault();
          if (this.props.onToggle) {
            this.props.onToggle(!this.props.isOpen);
          }
          if (this.props.onClick) {
            this.props.onClick(event);
          }
        },
        onKeyDown: (event) => {
          if (event.key === " ") {
            event.preventDefault();
            if (this.props.onToggle) {
              this.props.onToggle(!this.props.isOpen);
            }
          }
        },
        linkRef: this.linkRef,
        labelRef: this.labelRef,
        isCurrent: this.props.isCurrent,
        hasChildren: !!this.props.children,
        icon: this.props.icon,
        isMounted: this.state.isMounted,
        isLargeScreen: this.state.isLargeScreen,
        mobileLabel: this.props.mobileLabel,
        color: this.props.color,
        secondaryIcon: this.props.secondaryIcon,
        secondaryIconPurpose: this.props.secondaryIconPurpose,
        isOpen: this.props.isOpen,
        id: this.props.id,
        htmlRole: "button"
      }
    );
  }
  render() {
    return /* @__PURE__ */ React.createElement("li", { className: this.BEM(), style: this.state.itemStyle }, !this.props.children ? this.renderAnchor() : this.renderDropdownOrMegamenu(), this.state.isMounted && /* @__PURE__ */ React.createElement(MediaQuery, { minWidth: this.props.media.width.lg.min, onChange: this.handleMediaQueryChange }, null));
  }
}
const HeaderNavItemLink = React.forwardRef((props, propRef) => {
  const {
    label,
    url,
    onClick,
    onKeyDown,
    linkRef,
    labelRef,
    isCurrent,
    hasChildren,
    icon,
    isMounted,
    isLargeScreen,
    mobileLabel,
    color,
    secondaryIcon,
    secondaryIconPurpose,
    isOpen,
    id,
    htmlRole,
    ...rest
  } = props;
  const ref = useMergeRefs([propRef, linkRef]);
  const ariaCurrentValue = hasChildren ? true : "page";
  const ariaExpandedValue = isOpen ? "true" : "false";
  return /* @__PURE__ */ React.createElement(
    "a",
    {
      ...rest,
      href: url,
      onClick,
      onKeyDown,
      className: clsx("header__nav-link", {
        ["header__nav-link--".concat(color)]: !!color
      }),
      ref,
      "aria-current": isCurrent ? ariaCurrentValue : void 0,
      "aria-controls": isMounted && hasChildren && isOpen ? "".concat(id, "-megamenu") : void 0,
      "aria-expanded": isMounted && hasChildren ? ariaExpandedValue : void 0,
      role: htmlRole
    },
    icon && /* @__PURE__ */ React.createElement(Icon, { modifiers: icon, className: "header__nav-icon" }),
    /* @__PURE__ */ React.createElement("div", { className: "header__nav-label" }, /* @__PURE__ */ React.createElement("span", { className: "header__nav-label-inner", ref: labelRef }, isMounted && !isLargeScreen && mobileLabel ? mobileLabel : label, secondaryIcon && /* @__PURE__ */ React.createElement(Icon, { modifiers: secondaryIcon, className: clsx("header__nav-secondary-icon", secondaryIconPurpose) }))),
    hasChildren && /* @__PURE__ */ React.createElement(Icon, { modifiers: "#arrow-down", className: "header__nav-arrow" })
  );
});
export {
  HeaderNavItem
};
